import { zodResolver } from '@hookform/resolvers/zod';
import {
    BiAccordion,
    BiAccordionContent,
    BiAccordionHeader,
    BiButton,
    BiButtonGroup,
    BiFieldset,
    BiHeader,
    BiRadioButton,
    BiTypography,
} from 'bi-web-components-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { addToCart } from '../../api';
import { useCartContext } from '../../context/CartContext';
import useRedirectLoading from '../../hooks/useRedirectLoading';
import { AddToCartForm, addToCartFormScheema, Programme } from '../../types';
import {
    addVoiceTextElementToCart,
    getTermTranslationKey,
    isArrangementInCart,
    mapCampusCodeToCampus,
    mapCampusToCampusCode,
    openCart,
} from '../../utils';
import ErrorDialog from '../errorDialog/ErrorDialog';
import OneOptionContainer from '../programmePage/oneOptionContainer/oneOptionContainer';
import styles from './studyPlanner.module.scss';

import { FetcherError } from '@bino/utils/fetcher';
import { trackGAEventForApplication } from '@bino/utils/functions';

function getNumberOfDirtyFields(obj: Record<string, boolean>): number {
    return Object.values(obj).filter(Boolean).length;
}

function openYearSelectionAccordion() {
    const item = document.querySelector(`#secondandthirdId`);
    if (item) {
        item.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
        const event = new Event('openAccordion');
        item.dispatchEvent(event);
    }
}
interface StudyPlannerProps {
    programme: Programme;
}
export default function StudyPlanner({ programme }: StudyPlannerProps) {
    const [modalBlueIsOpen, setModalBlueIsOpen] = useState(false);
    const [modalRedIsOpen, setModalRedIsOpen] = useState(false);

    const { redirectLoading, startRedirectLoading } = useRedirectLoading();
    const { isAuthorized, cart, refreshCart, setGlobalCartError } = useCartContext();

    const { t } = useTranslation('cart');
    const { name, campuses } = programme;

    const isOneYearProgramme = name?.includes('studieår') || false;
    const arrangements = useMemo(() => programme?.arrangements ?? [], [programme.arrangements]);

    const [availableArrangements, setAvailableArrangements] = useState(arrangements);

    const [residences, terms, startYears, startYearsIncludingFirstYear] = useMemo(() => {
        const residences = [...new Set(arrangements?.map((arrangement) => arrangement.residenceCode))];
        const terms = [...new Set(arrangements?.map((arrangement) => arrangement.termCode))];
        const startYears = [
            ...new Set(
                arrangements
                    .filter(({ studentType }) => studentType !== '1' && studentType !== '0')
                    .map(({ studentType }) => studentType)
                    .sort((a, b) => parseInt(a) - parseInt(b)),
            ),
        ];
        const startYearsIncludingFirstYear = [...new Set(arrangements.map(({ studentType }) => studentType))];
        return [residences, terms, startYears, startYearsIncludingFirstYear];
    }, [arrangements]);

    const defaultValues = {
        campus: campuses.length === 1 ? campuses[0] : '',
        residence: residences.length === 1 ? residences[0] : '',
        term: terms.length === 1 ? terms[0] : '',
        studentType: startYearsIncludingFirstYear.length === 1 ? startYearsIncludingFirstYear[0] : '1',
    };

    const {
        handleSubmit,
        watch,
        reset,
        resetField,
        register,
        setError,
        formState: { dirtyFields, errors, isSubmitting, isLoading, isValidating },
    } = useForm<AddToCartForm>({
        defaultValues,
        resolver: zodResolver(addToCartFormScheema),
    });

    const [studentType, campus, residence, term] = watch(['studentType', 'campus', 'residence', 'term']);

    function handleCartError(error: unknown) {
        if (error instanceof FetcherError && error.status === 401) {
            return setGlobalCartError(error);
        }
        setModalRedIsOpen(true);
    }

    useEffect(() => {
        const filterAvailableArrangements = () => {
            if (!arrangements || arrangements.length === 0) return [];

            return arrangements.filter((arrangement) => {
                const validCampus =
                    (campus && mapCampusToCampusCode[campus].includes(arrangement.campusCode)) || !campus;

                const validResidence = arrangement.residenceCode === residence || !residence;
                const validTerm = arrangement.termCode === term || !term;
                const validStudentType = arrangement.studentType === studentType || studentType === '1';

                return validCampus && validResidence && validTerm && validStudentType;
            });
        };

        setAvailableArrangements(filterAvailableArrangements());
    }, [studentType, campus, residence, term, arrangements]);

    // Fetch arrangement from url and add to cart
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const arrangementBannerId = urlParams.get('a');
        // Remove arrangementBannerd from url
        const url = new URL(window.location.href);
        url.searchParams.delete('a');
        history.replaceState(null, '', url);

        const findArrangementAndAddToCart = async () => {
            if (arrangementBannerId) {
                const foundArrangement = arrangements.find(({ bannerId }) => bannerId === arrangementBannerId);

                if (foundArrangement) {
                    const exists = isArrangementInCart(foundArrangement.bannerId, cart.items);

                    if (exists) {
                        openCart();
                        addVoiceTextElementToCart(t('cart:errorAlreadyExists'));
                    } else {
                        try {
                            await addToCart([foundArrangement], isAuthorized);
                            await refreshCart();
                            openCart();
                            addVoiceTextElementToCart(t('cart:addedtocart'));
                        } catch (error) {
                            handleCartError(error);
                        }
                    }
                }
            }
        };

        findArrangementAndAddToCart();
    });

    const hasFiltersCampusResidenceTerm = campuses.length > 1 || residences.length > 1 || terms.length > 1;

    const onSubmit = handleSubmit(async (data: AddToCartForm) => {
        const selectedArrangement = availableArrangements.find((a) => {
            return (
                mapCampusToCampusCode[data.campus].includes(a.campusCode) &&
                data.studentType === a.studentType &&
                data.term === a.termCode &&
                data.residence === a.residenceCode
            );
        });

        if (selectedArrangement) {
            if (isArrangementInCart(selectedArrangement.bannerId, cart.items)) {
                return setModalBlueIsOpen(true);
            }

            try {
                trackGAEventForApplication('addToCart', [
                    {
                        id: selectedArrangement.productId,
                        name: name || 'Not Available',
                        campus: mapCampusCodeToCampus[selectedArrangement.campusCode],
                    },
                ]);
                await addToCart([selectedArrangement], isAuthorized);
                if (isAuthorized) {
                    addVoiceTextElementToCart(t('cart:addedtocart'));
                    await refreshCart();
                    openCart();
                    return resetForm();
                }
                startRedirectLoading();
            } catch (error) {
                handleCartError(error);
            }
        } else if (availableArrangements.length) {
            const notSelctedStudentType =
                availableArrangements.filter((arrangement) => arrangement.studentType !== '1').length > 0;

            if (notSelctedStudentType) {
                openYearSelectionAccordion();
                return setError('studentType', {
                    message: t('cart:secondandthirdValidator'),
                });
            }
        } else {
            setModalRedIsOpen(true);
        }
    });

    const isDisabled = (type: keyof AddToCartForm, value: string) => {
        const numberOfDirtyFields = getNumberOfDirtyFields(dirtyFields);
        const keyOfDirtyField = numberOfDirtyFields === 1 ? Object.keys(dirtyFields)[0] : null;
        const arrangements = keyOfDirtyField === type ? programme.arrangements : availableArrangements;
        switch (type) {
            case 'campus':
                return !arrangements?.some((arrangement) =>
                    mapCampusToCampusCode[value].includes(arrangement.campusCode),
                );
            case 'residence':
                return !arrangements?.some((arrangement) => arrangement.residenceCode === value);
            case 'term':
                return !arrangements?.some((arrangement) => arrangement.termCode === value);
            case 'studentType':
                return !arrangements?.some((arrangement) => arrangement.studentType === value);
            default:
                return false;
        }
    };

    function resetForm() {
        reset(defaultValues, { keepDirty: false, keepTouched: false });
    }

    function closeBlueModal() {
        setError('studentType', { message: undefined });
        setModalBlueIsOpen(false);
    }

    const termsText = (termCode: string) => {
        const [term, year] = getTermTranslationKey(termCode);
        return t(term) + ' ' + year;
    };

    const isWaiting = isSubmitting || isLoading || isValidating || redirectLoading;
    return (
        <>
            <div className={styles.container}>
                <form className={styles.formContainer} onSubmit={onSubmit}>
                    <BiHeader fontStyle='bold' level={2} className={styles.header}>
                        {t('cart:planner')}
                    </BiHeader>
                    {hasFiltersCampusResidenceTerm && (
                        <>
                            <BiTypography>{t('cart:adapt')}</BiTypography>
                            <div className={styles.filters}>
                                <div className={styles.filterGroup}>
                                    {campuses.length > 1 && (
                                        <BiFieldset
                                            required
                                            aria-label={t('cart:location')}
                                            errorMessage={t(errors.campus?.message || '')}
                                            label={t('cart:location')}
                                            error={Boolean(errors.campus)}
                                        >
                                            {campuses.map((item, idx) => (
                                                <BiRadioButton
                                                    key={idx}
                                                    value={item}
                                                    label={item}
                                                    disabled={isDisabled('campus', item)}
                                                    {...register('campus')}
                                                />
                                            ))}
                                        </BiFieldset>
                                    )}
                                    {residences.length > 1 && (
                                        <BiFieldset
                                            required
                                            aria-label={t('cart:studyload')}
                                            errorMessage={t(errors.residence?.message || '')}
                                            error={Boolean(errors.residence)}
                                            label={t('cart:studyload')}
                                        >
                                            {residences.map((item, idx) => (
                                                <BiRadioButton
                                                    key={idx}
                                                    value={item}
                                                    label={t(`residence.${item}`)}
                                                    disabled={isDisabled('residence', item)}
                                                    {...register('residence')}
                                                />
                                            ))}
                                        </BiFieldset>
                                    )}

                                    {terms.length > 1 && (
                                        <BiFieldset
                                            required
                                            aria-label={t('cart:term')}
                                            errorMessage={t(errors.term?.message || '')}
                                            label={t('cart:term')}
                                            error={Boolean(errors.term)}
                                        >
                                            {terms.map((item, idx) => (
                                                <BiRadioButton
                                                    key={idx}
                                                    value={item}
                                                    label={termsText(item)}
                                                    disabled={isDisabled('term', item)}
                                                    {...register('term')}
                                                />
                                            ))}
                                        </BiFieldset>
                                    )}
                                </div>
                                <div className={styles.reset}>
                                    <BiButton variant='text' name='resetFilters' onClick={() => resetForm()}>
                                        {t('cart:reset')}
                                    </BiButton>
                                </div>
                            </div>
                        </>
                    )}
                    <OneOptionContainer programme={programme} />

                    {startYears.length > 0 && !isOneYearProgramme && (
                        <BiAccordion backgroundColor='goldMuted' id='secondandthirdId'>
                            <BiAccordionHeader>
                                <BiTypography fontStyle='bold'>{t('cart:secondandthird')}</BiTypography>
                            </BiAccordionHeader>
                            <BiAccordionContent>
                                <div className={styles.secondAndThirdYearContentContainer}>
                                    <span>{t('cart:secondandthirdExpand1')}</span>
                                    <span>{t('cart:secondandthirdExpand2')}</span>
                                    <div className={styles.filters}>
                                        <div className={styles.filterGroup}>
                                            <BiFieldset
                                                aria-label={t('cart:selectStudyStart')}
                                                label={t('cart:selectStudyStart')}
                                                errorMessage={t(errors.studentType?.message || '')}
                                                error={Boolean(errors.studentType)}
                                            >
                                                {startYears.map((item, idx) => (
                                                    <BiRadioButton
                                                        key={idx}
                                                        value={item}
                                                        disabled={isDisabled('studentType', item)}
                                                        label={t(`cart:studentype${item}`)}
                                                        {...register('studentType')}
                                                    />
                                                ))}
                                            </BiFieldset>
                                        </div>

                                        <div className={styles.reset}>
                                            <BiButton
                                                variant='text'
                                                name='resetFilters'
                                                onClick={() => resetField('studentType')}
                                            >
                                                {t('cart:reset')}
                                            </BiButton>
                                        </div>
                                    </div>
                                </div>
                            </BiAccordionContent>
                        </BiAccordion>
                    )}

                    <BiButtonGroup alignment='center'>
                        <BiButton
                            stretch
                            name='addtocart'
                            type='submit'
                            aria-label={t('cart:addtocart')}
                            submitting={isWaiting}
                            disabled={isWaiting}
                        >
                            {t('cart:addtocart')}
                        </BiButton>
                    </BiButtonGroup>
                </form>
            </div>

            {modalBlueIsOpen && (
                <ErrorDialog
                    onClose={closeBlueModal}
                    message={errors?.studentType?.message || t('cart:errorAlreadyExists')}
                    heading={t('cart:infoHeading')}
                    icon='information'
                    color='blue'
                />
            )}

            {modalRedIsOpen && (
                <ErrorDialog
                    icon='warning'
                    color='red'
                    onClose={() => setModalRedIsOpen(false)}
                    heading={t('cart:errorHeading')}
                    message={t('cart:errorPostingToCart')}
                />
            )}
        </>
    );
}
